/**
 * BoardEvaluator - 盤面スコアリング
 *
 * ゲーム状態を数値化して評価する。
 * シミュレーション評価やGAの適応度計算に使用。
 */

import type { GameState, PlayerState } from '../../gameEngine/GameState';
import { getPlayer } from '../../gameEngine/GameState';
import type { PlayerNumber } from '../../../types/player';
import type { StatusEffect } from '../../../types/effect';
import type { MonsterInstance, CardWithId } from '../../../types/card';
import { hasHikou, hasTaiku } from '../../keywordAbilities';
import { STATUS_EFFECT_TYPES } from '../../statusEffects';

// =============================================================================
// 型定義
// =============================================================================

/** 評価パラメータ（GAで最適化可能） */
export interface EvaluationWeights {
  // ライフ
  myLifeWeight: number;
  oppLifeWeight: number;

  // フィールド - 自分
  myMonsterCountWeight: number;
  myTotalAttackWeight: number;
  myTotalHpWeight: number;

  // キーワード能力
  myFlightCountWeight: number;
  myAntiAirCountWeight: number;
  myGuardCountWeight: number;

  // 状態異常 - 自分モンスター
  myBuffCountWeight: number;
  myDebuffCountWeight: number;

  // 状態異常 - 相手モンスター
  oppDebuffCountWeight: number;

  // プレイヤー状態異常
  myPlayerDebuffWeight: number;
  oppPlayerDebuffWeight: number;

  // フィールド - 相手（脅威）
  oppMonsterCountWeight: number;
  oppTotalAttackWeight: number;

  // 深海領域
  deepSeaCardWeight: number;
  deepSeaDepthWeight: number;

  // SP
  activeSPWeight: number;
  restedSPWeight: number;
  maxSPWeight: number;

  // 手札
  handSizeWeight: number;
  costReductionWeight: number;

  // 墓地・デッキ
  graveyardSizeWeight: number;
  deckSizeWeight: number;
}

// =============================================================================
// デフォルト値
// =============================================================================

export const DEFAULT_WEIGHTS: EvaluationWeights = {
  // ライフ
  myLifeWeight: 1.40,
  oppLifeWeight: 0.64,

  // フィールド - 自分
  myMonsterCountWeight: 179.21,
  myTotalAttackWeight: 1.62,
  myTotalHpWeight: 0.32,

  // キーワード能力
  myFlightCountWeight: 133.60,
  myAntiAirCountWeight: 129.29,
  myGuardCountWeight: 42.44,

  // 状態異常 - 自分モンスター
  myBuffCountWeight: 22.03,
  myDebuffCountWeight: -75.66,

  // 状態異常 - 相手モンスター
  oppDebuffCountWeight: 63.97,

  // プレイヤー状態異常
  myPlayerDebuffWeight: -196.27,
  oppPlayerDebuffWeight: 200,

  // フィールド - 相手（脅威）
  oppMonsterCountWeight: -144.50,
  oppTotalAttackWeight: -0.62,

  // 深海領域
  deepSeaCardWeight: 8.46,
  deepSeaDepthWeight: 66.79,

  // SP
  activeSPWeight: 37.92,
  restedSPWeight: 8.32,
  maxSPWeight: 80.34,

  // 手札
  handSizeWeight: 53.26,
  costReductionWeight: 32.49,

  // 墓地・デッキ
  graveyardSizeWeight: 11.05,
  deckSizeWeight: 1.72,
};

// =============================================================================
// パラメータ範囲（GA用）
// =============================================================================

export interface ParamRange {
  min: number;
  max: number;
}

export const EVALUATION_PARAM_RANGES: Record<keyof EvaluationWeights, ParamRange> = {
  myLifeWeight: { min: 0, max: 2.0 },
  oppLifeWeight: { min: 0, max: 2.0 },
  myMonsterCountWeight: { min: 0, max: 300 },
  myTotalAttackWeight: { min: 0, max: 3.0 },
  myTotalHpWeight: { min: 0, max: 2.0 },
  myFlightCountWeight: { min: 0, max: 150 },
  myAntiAirCountWeight: { min: 0, max: 150 },
  myGuardCountWeight: { min: 0, max: 150 },
  myBuffCountWeight: { min: 0, max: 100 },
  myDebuffCountWeight: { min: -150, max: 0 },
  oppDebuffCountWeight: { min: 0, max: 100 },
  myPlayerDebuffWeight: { min: -300, max: 0 },
  oppPlayerDebuffWeight: { min: 0, max: 200 },
  oppMonsterCountWeight: { min: -200, max: 0 },
  oppTotalAttackWeight: { min: -2.0, max: 0 },
  deepSeaCardWeight: { min: 0, max: 100 },
  deepSeaDepthWeight: { min: 0, max: 150 },
  activeSPWeight: { min: 0, max: 50 },
  restedSPWeight: { min: 0, max: 30 },
  maxSPWeight: { min: 0, max: 100 },
  handSizeWeight: { min: 0, max: 80 },
  costReductionWeight: { min: 0, max: 80 },
  graveyardSizeWeight: { min: 0, max: 20 },
  deckSizeWeight: { min: 0, max: 20 },
};

// =============================================================================
// ヘルパー関数
// =============================================================================

/** バフ効果かどうか */
function isBuffEffect(effect: StatusEffect): boolean {
  const buffTypes: string[] = [
    STATUS_EFFECT_TYPES.ATK_UP,
    STATUS_EFFECT_TYPES.HP_UP,
    STATUS_EFFECT_TYPES.GUARD,
    STATUS_EFFECT_TYPES.INVINCIBLE,
    STATUS_EFFECT_TYPES.AWAKENED,
  ];
  return buffTypes.includes(effect.type);
}

/** デバフ効果かどうか */
function isDebuffEffect(effect: StatusEffect): boolean {
  const debuffTypes: string[] = [
    STATUS_EFFECT_TYPES.FREEZE,
    STATUS_EFFECT_TYPES.SLEEP,
    STATUS_EFFECT_TYPES.STUN,
    STATUS_EFFECT_TYPES.SILENCE,
    STATUS_EFFECT_TYPES.WET,
    STATUS_EFFECT_TYPES.THUNDER,
    STATUS_EFFECT_TYPES.CORRODE,
    STATUS_EFFECT_TYPES.PARASITE,
  ];
  return debuffTypes.includes(effect.type);
}

/** プレイヤーのデバフ数をカウント */
function countPlayerDebuffs(statusEffects: StatusEffect[] | undefined): number {
  if (!statusEffects) return 0;
  return statusEffects.filter(e => e.type === STATUS_EFFECT_TYPES.POISON).length;
}

/** 守護状態かどうか */
function hasGuardStatus(monster: MonsterInstance): boolean {
  if (!monster.statusEffects) return false;
  return monster.statusEffects.some(e => e.type === STATUS_EFFECT_TYPES.GUARD);
}

/** 手札のコスト軽減総量を計算 */
function calculateTotalCostReduction(
  _hand: CardWithId[],
  _playerState: PlayerState,
  _state: GameState
): number {
  // TODO: フィールドカード、フェイズカード、プレイヤーバフによるコスト軽減を計算
  // 実装は既存のgetModifiedCostInfo等を参照
  // 現時点では簡易実装として0を返す
  return 0;
}

/** 最大SP（SPトークン数）を計算 */
function calculateMaxSP(playerState: PlayerState): number {
  // activeSP + restedSP + 減少分を考慮
  // 基本的にはactiveSP + restedSPが現在の最大値に近い
  return playerState.activeSP + playerState.restedSP;
}

// =============================================================================
// メイン評価関数
// =============================================================================

/**
 * 盤面をスコア化
 *
 * @param state - ゲーム状態
 * @param player - 評価対象のプレイヤー
 * @param weights - 評価パラメータ（省略時はデフォルト）
 * @returns 盤面スコア（高いほど有利）
 */
export function evaluateBoard(
  state: GameState,
  player: PlayerNumber,
  weights: EvaluationWeights = DEFAULT_WEIGHTS
): number {
  let score = 0;
  const my = getPlayer(state, player);
  const opp = getPlayer(state, player === 1 ? 2 : 1);

  // === ライフ ===
  score += my.life * weights.myLifeWeight;
  score -= opp.life * weights.oppLifeWeight;

  // === 自分のフィールド ===
  const myMonsters = my.field.filter((m): m is MonsterInstance => m !== null);
  score += myMonsters.length * weights.myMonsterCountWeight;

  let myTotalAtk = 0;
  let myTotalHp = 0;
  let myFlightCount = 0;
  let myAntiAirCount = 0;
  let myGuardCount = 0;
  let myBuffCount = 0;
  let myDebuffCount = 0;

  for (const monster of myMonsters) {
    myTotalAtk += monster.currentAttack || monster.attack || 0;
    myTotalHp += monster.currentHp || monster.hp || 0;

    // キーワード能力
    if (hasHikou(monster)) myFlightCount++;
    if (hasTaiku(monster)) myAntiAirCount++;
    if (hasGuardStatus(monster)) myGuardCount++;

    // 状態異常カウント
    if (monster.statusEffects) {
      for (const effect of monster.statusEffects) {
        if (isBuffEffect(effect)) myBuffCount++;
        if (isDebuffEffect(effect)) myDebuffCount++;
      }
    }
  }

  score += myTotalAtk * weights.myTotalAttackWeight;
  score += myTotalHp * weights.myTotalHpWeight;
  score += myFlightCount * weights.myFlightCountWeight;
  score += myAntiAirCount * weights.myAntiAirCountWeight;
  score += myGuardCount * weights.myGuardCountWeight;
  score += myBuffCount * weights.myBuffCountWeight;
  score += myDebuffCount * weights.myDebuffCountWeight;

  // === 相手のフィールド（脅威）===
  const oppMonsters = opp.field.filter((m): m is MonsterInstance => m !== null);
  score += oppMonsters.length * weights.oppMonsterCountWeight;

  let oppTotalAtk = 0;
  let oppDebuffCount = 0;
  for (const monster of oppMonsters) {
    oppTotalAtk += monster.currentAttack || monster.attack || 0;
    if (monster.statusEffects) {
      for (const effect of monster.statusEffects) {
        if (isDebuffEffect(effect)) oppDebuffCount++;
      }
    }
  }
  score += oppTotalAtk * weights.oppTotalAttackWeight;
  score += oppDebuffCount * weights.oppDebuffCountWeight;

  // === プレイヤー状態異常 ===
  const myPlayerDebuffs = countPlayerDebuffs(my.statusEffects);
  const oppPlayerDebuffs = countPlayerDebuffs(opp.statusEffects);
  score += myPlayerDebuffs * weights.myPlayerDebuffWeight;
  score += oppPlayerDebuffs * weights.oppPlayerDebuffWeight;

  // === 深海領域 ===
  if (my.deepSeaZone && my.deepSeaZone.cards) {
    const deepSeaCards = my.deepSeaZone.cards;
    score += deepSeaCards.length * weights.deepSeaCardWeight;

    let totalDepth = 0;
    for (const card of deepSeaCards) {
      totalDepth += card.depth || 0;
    }
    score += totalDepth * weights.deepSeaDepthWeight;
  }

  // === SP ===
  score += my.activeSP * weights.activeSPWeight;
  score += my.restedSP * weights.restedSPWeight;
  const maxSP = calculateMaxSP(my);
  score += maxSP * weights.maxSPWeight;

  // === 手札 ===
  score += my.hand.length * weights.handSizeWeight;
  const costReduction = calculateTotalCostReduction(my.hand, my, state);
  score += costReduction * weights.costReductionWeight;

  // === 墓地・デッキ ===
  score += my.graveyard.length * weights.graveyardSizeWeight;
  score += my.deck.length * weights.deckSizeWeight;

  return score;
}

/**
 * 盤面スコアの差分を計算
 *
 * @param beforeState - 変更前の状態
 * @param afterState - 変更後の状態
 * @param player - 評価対象のプレイヤー
 * @param weights - 評価パラメータ
 * @returns スコア差分（正なら改善）
 */
export function evaluateBoardDifference(
  beforeState: GameState,
  afterState: GameState,
  player: PlayerNumber,
  weights: EvaluationWeights = DEFAULT_WEIGHTS
): number {
  const beforeScore = evaluateBoard(beforeState, player, weights);
  const afterScore = evaluateBoard(afterState, player, weights);
  return afterScore - beforeScore;
}

/**
 * ランダムな評価パラメータを生成（GA用）
 */
export function createRandomWeights(): EvaluationWeights {
  const weights: Partial<EvaluationWeights> = {};
  const weightsRecord = weights as unknown as Record<string, number>;

  for (const [key, range] of Object.entries(EVALUATION_PARAM_RANGES)) {
    const value = range.min + Math.random() * (range.max - range.min);
    weightsRecord[key] = value;
  }

  return weights as EvaluationWeights;
}

/**
 * 評価パラメータをクローン
 */
export function cloneWeights(weights: EvaluationWeights): EvaluationWeights {
  return { ...weights };
}

/**
 * 評価パラメータを正規化（範囲外の値を修正）
 */
export function normalizeWeights(weights: EvaluationWeights): EvaluationWeights {
  const normalized = cloneWeights(weights);
  const normalizedRecord = normalized as unknown as Record<string, number>;

  for (const [key, range] of Object.entries(EVALUATION_PARAM_RANGES)) {
    const currentValue = normalizedRecord[key];
    normalizedRecord[key] = Math.max(
      range.min,
      Math.min(range.max, currentValue)
    );
  }

  return normalized;
}
